import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Language = 'en' | 'es';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  investorName: string;
  setInvestorName: (name: string) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations = {
  en: {
    // Hero Section
    'hero.title': 'Artificial Intelligence in your Medical Clinic',
    'hero.subtitle': 'Revolution',
    'hero.description': 'You just found the right partner for your clinic’s AI adoption. We test it in our lab—you adopt what works. Start today.',
    'hero.explore': 'Book a Call',
    'hero.learnMore': 'Chat with AI Agent',
    
    // AI Digital Clinic Assessment Hero
    'hero.assessment.title': 'AI Digital Clinic Assessment',
    'hero.assessment.subtitle': 'Transform Your Medical Practice with Data-Driven Insights',
    'hero.assessment.description': 'Get a complimentary audit of your clinic\'s entire digital ecosystem and AI-driven strategies to grow your practice.',
    
    // AI Voice Agent Page
    'aiVoiceAgent.page.title': 'AI Voice Agent - Bella Voice Receptionist | RBI AI',
    'aiVoiceAgent.page.description': 'Meet Bella, your AI-powered voice receptionist. Handle patient calls 24/7, schedule appointments, answer questions, and provide exceptional service without human intervention.',
    'aiVoiceAgent.hero.title': 'Meet Bella: Your AI Voice Receptionist',
    'aiVoiceAgent.hero.subtitle': 'Let AI handle your phone calls while your team focuses on patient care. Bella answers questions, books appointments, and provides information—all with a natural, human-like voice.',
    
    // Conversational AI Page
    'conversationalAI.page.title': 'Conversational AI - WhatsApp & Instagram Agents | RBI AI',
    'conversationalAI.page.description': 'AI-powered conversational agents for WhatsApp and Instagram. Automate patient communication, appointment booking, and follow-ups 24/7 with intelligent chat automation.',
      'conversationalAI.hero.title': 'AI Agents for WhatsApp & Instagram',
      'conversationalAI.hero.subtitle': 'Automate patient conversations and appointment bookings across your most-used messaging platforms. Let AI handle inquiries, schedule appointments, and engage patients 24/7—while your team focuses on care.',
      
    // Meta Ads Page
    'metaAds.page.title': 'Meta Ads Management - Google & Instagram Advertising | RBI AI',
    'metaAds.page.description': 'Expert Google Ads and Instagram advertising management for clinics. Real-time campaign tracking, optimization, and ROI-focused strategies to grow your patient base.',
    'metaAds.hero.title': 'Data-Driven Advertising That Delivers Patients',
    'metaAds.hero.subtitle': 'Professional management of your Google and Instagram ad campaigns. We optimize every dollar to maximize patient acquisition, track performance in real-time, and deliver measurable ROI for your practice.',
    
    // AI Audit Page
    'aiAudit.page.title': 'AI Audit - Comprehensive Healthcare Practice Analysis | RBI AI',
    'aiAudit.page.description': 'Get a complete AI-powered audit of your healthcare practice. Competitor benchmarking, automation analysis, AI integration opportunities, and digital presence optimization.',
    'aiAudit.hero.title': 'Discover Where AI Can Transform Your Practice',
    'aiAudit.hero.subtitle': 'Comprehensive analysis of your clinic\'s performance, automation potential, and growth opportunities. Get actionable insights on competitor benchmarking, AI integration, workflows, and digital presence—all powered by advanced analytics.',
    
    // Rotating Headlines
    'hero.rotating.line1': 'AI Digital Assessment',
    'hero.rotating.line2': 'Stem Cell Clinics',
    'hero.rotating.line3': 'Plastic Surgeons',
    'hero.rotating.line4': 'Dentists',
    'hero.rotating.line5': 'Powered by AI',
    
    
    
    // Hero Video Section (EN)
    'heroVideo.title1': 'Augmented Reality',
    'heroVideo.title2': 'Experience',
    'heroVideo.description': 'It\'s not just about living longer, but living better. With AI applied to medicine and longevity, we transform data into decisions and technology into years of healthy life. <strong>Discover our augmented reality experience</strong> and explore how RBI AI connects you with solutions that today build tomorrow.',
    'heroVideo.arButton': 'View AR Experience',
    'heroVideo.qrText': 'Scan with your mobile to see the AR experience',
    
    // Dental (EN)
    'dental.hero.title': 'AI That Fills Your Dental Calendar',
    'dental.hero.subtitle': 'From interest to booked patients — automated.',
    'dental.hero.description': 'We apply AI content, agents, and automations tailored for dental clinics.',
    'dental.hero.ctaPrimary': 'Book Dental AI Call',
    'dental.hero.ctaSecondary': 'Request a Dental Quote',
    'dental.hero.videoTitle': 'Dental AI demo video',

    // Dental Tourism planner imagery (EN)
    'dentalTourism.images.step1': 'Costa Rica dental clinic — patient or caregiver',
    'dentalTourism.images.step2': 'Costa Rica dental procedures consultation',
    'dentalTourism.images.step3': 'Costa Rica dental trip — timeline and budget planning',
    'dentalTourism.images.step4': 'Travel basics — flights to San José, Costa Rica',
    'dentalTourism.images.step5': 'Lodging and tours in Costa Rica for dental trip',
    'dentalTourism.images.step6': 'Concierge support for Costa Rica dental tourism',
    'dentalTourism.images.step7': 'Dental trip summary — Costa Rica itinerary',


// Rotating Headlines (EN)
    'hero.rotating.1': 'You Just<br />Found the<br />Right AI Partner<br />for Your Clinic',
    'hero.rotating.2': 'Your Clinic’s AI Journey Starts Today',
    'hero.rotating.3': 'Become an<br />AI‑Driven<br />Healthcare<br />Clinic',
    'hero.rotating.4': 'Stand out<br />with AI‑first<br />marketing that<br />adapts daily',
    'hero.rotating.5': 'Become the<br />ChatGPT<br />authority<br />in your area',
    'hero.rotating.6': 'AI<br />specialists<br />worldwide building<br />for your industry',
    'hero.rotating.7': 'We test it<br />in our lab<br />— you adopt<br />what works',
    'hero.rotating.8': 'Train an<br />AI<br />receptionist<br />to book patients 24/7',
    'hero.rotating.9': 'Real examples, real results — not generic “AI” hype',
    'hero.rotating.10': 'We’re building the future for those who want to evolve',
    'hero.rotating.11': 'Our lab<br />builds for<br />hospitals; SMEs<br />scale with RBI AI',
    'hero.rotating.12': 'Join the<br />Medical AI<br />Community and learn<br />from real use cases',
    // Stats Section
    'stats.title': '10 Core Areas We Analyze',
    'stats.professionals': 'Digital touchpoints audited including website performance, SEO fundamentals, backlinks, local signals, and mobile optimization',
    'stats.increase': 'of stem cell clinics lack proper schema markup and AEO optimization for high-intent medical tourism searches',
    'stats.days': 'comprehensive competitor benchmarking against DVCStem, Cell Medicine, Fountain Life, and 4 other global leaders',
    'stats.getStarted': 'START YOUR AUDIT',
    
    // Success Metrics
    'metrics.title': 'Digital Presence & SEO Health',
    'metrics.subtitle': 'Technical Performance Audit',
    'metrics.description': 'We analyze website speed (LCP, CLS), on-page SEO, schema markup, domain authority, organic traffic, backlink profile, and technical gaps affecting search visibility.',
    
    // Opportunities
    'opportunities.title': 'Competitor Benchmarking &',
    'opportunities.subtitle': 'Market Positioning',
    'opportunities.description': 'Compare your clinic against major players in regenerative medicine. We analyze traffic, domain authority, backlinks, paid media activity, content strategy, and brand positioning.',
    
    // Automated Workflows
    'workflows.title': 'Automation & AI Integration',
    'workflows.subtitle': 'Opportunity Analysis',
    'workflows.description': 'Identify gaps in your CRM, lead nurturing flows, review automation, and patient journey sequencing. Get actionable recommendations for AI-driven engagement tools and predictive analytics.',
    
    // Pricing
    'pricing.title': 'PAYMENT PLANS',
    'pricing.choosePlan': 'Choose Your Plan',
'pricing.subtitle': 'Select the number of doctors in your clinic for exact pricing estimations.',
    'pricing.popular': 'Most Popular',
    'pricing.implementation': 'Implementation per Dr:',
    'pricing.month': '/month',
    'pricing.implementedBy': 'Implemented by',
    'pricing.base': 'Base:',
    'pricing.addons': 'Add-ons:',
    'pricing.startWith': 'Start with',
    'pricing.addonsTitle': 'Add-ons:',
    'pricing.add': 'Add',
    'pricing.selected': 'Selected Plan:',
    'pricing.basePlan': 'Base Plan:',
    'pricing.addedAddons': 'Added Add-ons:',
    'pricing.monthlyTotal': 'Monthly Total:',
    'pricing.disclaimer1': '*Each additional dr. would have a Zapiy Ai sub-account. Implementation includes Assistant training and flow configuration.',
    'pricing.perDoctorDisclaimer': '*Prices are per doctor',
    'pricing.implementationFee': 'Implementation Fee:',
    'pricing.disclaimer2': '** Total available for all Drs - The clinic can increase budget when desired',
    'pricing.remove': 'Remove',
    'pricing.selectPlanFirst': 'Please select a plan first',
    'pricing.requestSent': 'Request Sent Successfully!',
    'pricing.requestDescription': 'We will contact you within 24 hours.',
    'pricing.sendError': 'Error Sending',
    'pricing.sendErrorDescription': 'Please try again or contact us directly.',
    'pricing.contactInfo': 'Contact Information',
    'pricing.fullName': 'Full Name',
    'pricing.fullNamePlaceholder': 'Enter your full name',
    'pricing.email': 'Email',
    'pricing.emailPlaceholder': 'Enter your email',
    'pricing.clinicName': 'Clinic Name',
    'pricing.clinicNamePlaceholder': 'Enter your clinic name',
    'pricing.phoneNumber': 'Phone Number',
    'pricing.phoneNumberPlaceholder': 'Enter your phone number',
    'pricing.additionalMessage': 'Additional Message',
    'pricing.additionalMessagePlaceholder': 'Any special requirements or additional questions?',
    'pricing.sendRequest': 'Send Request to RBI AI',
    
    // Plans
    'plans.basic.name': 'BASIC',
    'plans.basic.package': 'Package 1',
    'plans.basic.subtitle': 'Automation + AI Assistant',
    'plans.basic.description': 'AI Assistant + Base Automation',
    'plans.professional.name': 'PROFESSIONAL',
    'plans.professional.package': 'Package 2',
    'plans.professional.subtitle': 'Meta Ads + Graphics',
    'plans.professional.description': 'Package 1 + Meta Ads + Graphics',
    'plans.premium.name': 'PREMIUM',
    'plans.premium.package': 'Package 3',
    'plans.premium.subtitle': 'Google Ads + Graphics',
    'plans.premium.description': 'Package 2 + Google Ads + SEO',
    'plans.included': 'Included',
    
    // Features
    'features.aiAssistant': 'Includes AI Assistant WhatsApp + IG',
    'features.automations': 'WhatsApp & Email Automations',
    'features.googleReviews': 'Automatic Google Reviews',
    'features.technicalSupport': 'Zapiy Technical Support',
    'features.metaAds': 'Meta Ads Campaigns',
    'features.monthlyMeeting': '1 Monthly Report Meeting',
    'features.aiSpecialistSupport': 'AI Specialist Support',
    
    'features.graphicPieces': '6 Monthly graphic pieces',
    'features.metaCrmAi': 'Meta – CRM – AI',
    'features.metaDashboard': 'Performance Dashboard (Meta)',
    'features.googleAds': 'Google Ads Campaigns',
    
    'features.ga4Tracking': 'GA4 + advanced tracking',
    'features.freeSeminar': 'Free AI Seminar',
    
    // Add-ons
    'addons.webSupport': 'Institutional Web Support',
    'addons.webSupportPrice': '$350/month (per clinic)',
    'addons.bellaVoice': 'Bella AI Voice Agent',
    'addons.bellaVoicePrice': '$295/month per doctor',
    'addons.whatsappBlast': 'WhatsApp Blast',
    'addons.whatsappBlastPrice': '$97/month per doctor',
    
    // FAQ
    'faq.title': 'Frequently Asked Questions',
    'faq.subtitle': 'Get answers to common questions about Bella AI implementation and benefits',
    'faq.stillQuestions': 'Still have questions?',
    'faq.teamReady': 'Our team is ready to answer any specific questions about your clinic\'s needs.',
    'faq.schedule': 'Schedule a Consultation',
    
    // Audit FAQ
    'faq.audit.title': 'Audit Process FAQ',
    'faq.audit.subtitle': 'Get answers about our AI Digital Clinic Assessment process',
    'faq.audit.q1': 'How long does it take to receive my assessment?',
    'faq.audit.a1': 'You will receive your comprehensive AI audit report within 72 hours of completing the assessment form. Our team analyzes your clinic\'s digital presence, AI opportunities, and growth potential to deliver actionable insights.',
    'faq.audit.q2': 'Do I need to complete a form?',
    'faq.audit.a2': 'Yes, you\'ll need to complete our digital assessment form. This helps us understand your clinic\'s current setup, marketing channels, booking systems, and AI readiness. The form takes about 10-15 minutes to complete.',
    'faq.audit.q3': 'Will someone walk me through the results?',
    'faq.audit.a3': 'Absolutely! After you receive your audit report, we schedule a consultation call where our AI specialists walk you through the findings step-by-step. We\'ll explain opportunities, prioritize recommendations, and answer all your questions.',
    'faq.audit.q4': 'What does the audit include?',
    'faq.audit.a4': 'The audit covers 10 core areas: competitor benchmarking, real-time analytics, reputation management, campaign ROI, AI integration opportunities, patient journey analysis, automated workflows, digital presence, SEO optimization, and call tracking.',
    'faq.audit.q5': 'Is the audit really free?',
    'faq.audit.a5': 'Yes, the AI Digital Clinic Assessment is completely complimentary. There\'s no obligation to purchase any services. We provide the audit to help clinics understand their AI opportunities.',
    'faq.audit.q6': 'Is my clinic data secure?',
    'faq.audit.a6': 'Absolutely. All data shared during the audit is encrypted, stored securely, and used only for your assessment. We follow strict privacy protocols and never share your information with third parties.',
    'faq.audit.q7': 'What happens after the audit?',
    'faq.audit.a7': 'After the consultation call, you\'ll receive a detailed action plan. You can choose to implement recommendations yourself, work with your existing team, or partner with us. There\'s no pressure—the insights are yours to use however you prefer.',
    
    // Bella AI Voice Agent
    'bella.title': 'Try Bella AI Voice Agent',
    'bella.subtitle': 'Experience how Bella AI can transform your clinic\'s communication',
    'bella.name': 'Bella - AI Receptionist',
    'bella.status': 'Online 24/7',
    'bella.greeting': 'Hi! I\'m Bella, your AI assistant. How can I help you today?',
    'bella.talkButton': 'Talk to Bella',
    'bella.listening': 'Listening...',
    'bella.scheduleDemo': 'BOOK A CALL',
    'bella.scheduleTitle': 'Book Your Bella AI Call',
    'bella.scheduleSubtitle': 'Choose a time that works best for you',
    'bella.try': 'START HERE',

    // Compliance Section
    'compliance.title': 'Enterprise-Grade Security & Compliance',
    'compliance.subtitle': 'Your patient data is protected by industry-leading security standards and full regulatory compliance for healthcare organizations.',
    'compliance.hipaa.title': 'HIPAA Compliant',
    'compliance.hipaa.description': 'Built for U.S. healthcare providers with complete PHI protection and Business Associate Agreements.',
    'compliance.hipaa.feature1': 'Business Associate Agreements (BAAs) available',
    'compliance.hipaa.feature2': 'End-to-end encryption (TLS 1.2+, AES-256)',
    'compliance.hipaa.feature3': 'Comprehensive audit logs and access controls',
    'compliance.gdpr.title': 'GDPR Compliant',
    'compliance.gdpr.description': 'Full compliance with European data protection regulations, ensuring patient privacy rights.',
    'compliance.gdpr.feature1': 'Right to access, rectification, and erasure',
    'compliance.gdpr.feature2': 'Consent management and data portability',
    'compliance.gdpr.feature3': 'EU data residency options available',
    'compliance.learnMore': 'Learn More',
    'compliance.trustMessage': 'Your patient data is safe with us',
    'compliance.certifications': 'SOC 2 Type II • ISO 27001 • GDPR Ready',

    // AI Conversation Hero
    'aiHero.title': 'AI Conversational Agents for Patient Booking',
    'aiHero.description': 'After capturing leads through our engaging medical content, our AI conversational agents seamlessly take over to nurture and convert these prospects into booked appointments across WhatsApp and Instagram.',
    'aiHero.whatsapp.caption': 'Instant appointment booking through WhatsApp with smart conversation flow',
    'aiHero.instagram.caption': 'Natural patient engagement on Instagram with seamless booking integration',
    'aiHero.features.instant.title': 'Instant Response',
    'aiHero.features.instant.desc': 'Our AI agents respond to messages within seconds across WhatsApp and Instagram, ensuring no potential patient is left waiting. Available 24/7.',
    'aiHero.features.qualify.title': 'Smart Qualification',
    'aiHero.features.qualify.desc': 'Advanced qualification process that understands patient needs, symptoms, and insurance information to route them to the right healthcare provider.',
    'aiHero.features.booking.title': 'Seamless Booking',
    'aiHero.features.booking.desc': 'Direct integration with practice management systems for real-time appointment scheduling, confirmation, and reminder automation.',
    'aiHero.features.hipaa.title': 'HIPAA Compliant',
    'aiHero.features.hipaa.desc': 'All conversations are encrypted and HIPAA-compliant, ensuring patient privacy and data security throughout the entire booking process.',
    'aiHero.cta.title': 'Ready to Automate Your Patient Bookings?',
    'aiHero.cta.desc': 'Our AI conversational agents have helped healthcare practices increase their booking rates by 340% while reducing administrative overhead by 60%.',
    'aiHero.stats.conversion': 'Conversion Rate',
    'aiHero.stats.responseTime': 'Average Response Time',
    'aiHero.stats.availability': 'Availability',

    // AI Consultancy Page
    'aiConsultancy.page.title': 'AI Consultancy - Strategic AI Implementation for Healthcare | RBI AI',
    'aiConsultancy.page.description': 'Expert AI strategy and implementation guidance for medical practices. Get comprehensive ROI analysis, compliance assurance, and step-by-step AI adoption planning.',
    'aiConsultancy.hero.badge': 'Strategic AI Guidance',
    'aiConsultancy.hero.title': 'Transform Your Practice with Expert AI Consultancy',
    'aiConsultancy.hero.subtitle': 'Strategic planning, implementation roadmaps, and ROI optimization for successful AI adoption in your healthcare practice.',
    'aiConsultancy.hero.cta': 'Schedule Consultation',
    'aiConsultancy.services.title': 'Comprehensive AI Consulting Services',
    'aiConsultancy.services.subtitle': 'End-to-end guidance for your AI transformation journey',
    'aiConsultancy.features.strategy.title': 'AI Strategy Development',
    'aiConsultancy.features.strategy.desc': 'Custom AI roadmaps aligned with your practice goals, patient demographics, and growth objectives.',
    'aiConsultancy.features.assessment.title': 'Technology Assessment',
    'aiConsultancy.features.assessment.desc': 'Comprehensive audit of your current systems, identifying AI integration opportunities and technology gaps.',
    'aiConsultancy.features.roi.title': 'ROI Analysis & Forecasting',
    'aiConsultancy.features.roi.desc': 'Detailed financial modeling showing expected returns, cost savings, and revenue growth from AI implementation.',
    'aiConsultancy.features.implementation.title': 'Implementation Planning',
    'aiConsultancy.features.implementation.desc': 'Step-by-step execution plans, timeline management, and resource allocation for smooth AI deployment.',
    'aiConsultancy.features.innovation.title': 'Innovation Advisory',
    'aiConsultancy.features.innovation.desc': 'Stay ahead with insights on emerging AI technologies, industry trends, and competitive advantages.',
    'aiConsultancy.features.compliance.title': 'Compliance Assurance',
    'aiConsultancy.features.compliance.desc': 'Ensure all AI implementations meet HIPAA, GDPR, and other regulatory requirements from day one.',
    'aiConsultancy.process.title': 'Our Consulting Process',
    'aiConsultancy.process.step1.title': 'Discovery & Assessment',
    'aiConsultancy.process.step1.desc': 'Deep dive into your practice operations, challenges, and AI readiness with comprehensive stakeholder interviews.',
    'aiConsultancy.process.step2.title': 'Strategy Development',
    'aiConsultancy.process.step2.desc': 'Create a tailored AI roadmap with prioritized initiatives, timelines, and clear success metrics.',
    'aiConsultancy.process.step3.title': 'Implementation Support',
    'aiConsultancy.process.step3.desc': 'Guide your team through deployment with hands-on support, training coordination, and change management.',
    'aiConsultancy.process.step4.title': 'Optimization & Growth',
    'aiConsultancy.process.step4.desc': 'Continuous monitoring, performance optimization, and strategic guidance as your AI capabilities mature.',
    'aiConsultancy.cta.title': 'Ready to Build Your AI Strategy?',
    'aiConsultancy.cta.subtitle': 'Schedule a consultation to discuss your practice\'s unique AI opportunities and challenges.',
    'aiConsultancy.cta.button': 'Book Strategy Session',

    // Staff Training Page
    'staffTraining.page.title': 'AI Staff Training - Healthcare AI Adoption Programs | RBI AI',
    'staffTraining.page.description': 'Comprehensive AI training programs for medical staff. Build confidence, drive adoption, and maximize ROI with hands-on workshops and certification courses.',
    'staffTraining.hero.badge': 'Empower Your Team',
    'staffTraining.hero.title': 'AI Training Programs for Healthcare Teams',
    'staffTraining.hero.subtitle': 'Equip your staff with the knowledge and skills to leverage AI effectively, increasing adoption rates and maximizing the value of your AI investments.',
    'staffTraining.hero.cta': 'Explore Training Programs',
    'staffTraining.programs.title': 'Comprehensive Training Programs',
    'staffTraining.programs.subtitle': 'From AI fundamentals to advanced implementation strategies',
    'staffTraining.programs.fundamentals.title': 'AI Fundamentals for Healthcare',
    'staffTraining.programs.fundamentals.desc': 'Introduction to AI concepts, use cases in healthcare, and understanding how AI can enhance patient care and operations.',
    'staffTraining.programs.workflows.title': 'AI-Enhanced Workflows',
    'staffTraining.programs.workflows.desc': 'Learn to integrate AI tools into daily operations, optimize patient engagement, and automate routine tasks effectively.',
    'staffTraining.programs.tools.title': 'Hands-On Tool Training',
    'staffTraining.programs.tools.desc': 'Practical training on specific AI platforms, CRM systems, conversational agents, and analytics dashboards.',
    'staffTraining.programs.advanced.title': 'Advanced AI Implementation',
    'staffTraining.programs.advanced.desc': 'Deep dive into complex AI strategies, custom automation building, and data-driven decision making.',
    'staffTraining.programs.certification.title': 'AI Certification Program',
    'staffTraining.programs.certification.desc': 'Earn recognized credentials demonstrating proficiency in healthcare AI tools and best practices.',
    'staffTraining.programs.leadership.title': 'Leadership & Change Management',
    'staffTraining.programs.leadership.desc': 'Training for practice leaders on driving AI adoption, managing change, and building an AI-ready culture.',
    'staffTraining.benefits.title': 'Training Benefits',
    'staffTraining.benefits.adoption': 'Faster AI adoption rates across your entire team',
    'staffTraining.benefits.efficiency': 'Improved operational efficiency through proper tool utilization',
    'staffTraining.benefits.confidence': 'Increased staff confidence in using AI technologies',
    'staffTraining.benefits.innovation': 'Culture of innovation and continuous improvement',
    'staffTraining.benefits.retention': 'Higher staff retention through professional development',
    'staffTraining.benefits.roi': 'Maximized ROI on AI investments through skilled implementation',
    'staffTraining.format.title': 'Flexible Training Formats',
    'staffTraining.format.onsite.title': 'On-Site Workshops',
    'staffTraining.format.onsite.desc': 'Interactive, hands-on training sessions conducted at your practice for focused team learning.',
    'staffTraining.format.virtual.title': 'Virtual Sessions',
    'staffTraining.format.virtual.desc': 'Live online training with real-time interaction, perfect for distributed teams or busy schedules.',
    'staffTraining.format.hybrid.title': 'Hybrid Programs',
    'staffTraining.format.hybrid.desc': 'Combination of on-site kickoffs, virtual sessions, and self-paced modules for maximum flexibility.',
    'staffTraining.cta.title': 'Invest in Your Team\'s AI Skills',
    'staffTraining.cta.subtitle': 'Contact us to design a custom training program tailored to your practice\'s needs and goals.',
    'staffTraining.cta.button': 'Request Training Proposal',

    // ROI Calculator
    'roi.title': 'Still worried about investing in AI ?',
    'roi.subtitle': 'Discover how many patients you need to recover your investment and generate profits',
    'roi.cardTitle': 'Your Clinic Data',
    'roi.avgConsultation': 'Average consultation cost (USD)',
    'roi.avgConsultation.help': 'Include consultations, procedures and average services',
    'roi.selectPackage': 'Select your package',
    'roi.choosePlaceholder': 'Choose a package',
    'roi.implementation': 'Implementation',
    'roi.calculate': 'Calculate ROI',
    'roi.clear': 'Clear',
    'roi.results.title': 'Analysis Results',
    'roi.results.patientsToRecover': 'Patients to recover initial investment',
    'roi.results.monthlyPatients': 'Monthly patients to cover costs',
    'roi.results.potentialRevenue': 'Potential monthly revenue:',
    'roi.results.netProfit': 'Net monthly profit:',
    'roi.results.monthlyROI': 'Monthly ROI:',
    'roi.results.breakEven': 'Break Even Point',
    'roi.results.breakEven.prefix': "You'll recover your initial investment in approximately ",
    'roi.results.breakEven.suffix': ' business days (assuming 8 patients per day)',
    'roi.results.summary': 'Executive Summary:',
    'roi.empty': 'Complete the data to see your personalized ROI analysis',
    'roi.alertFillFields': 'Please complete all fields',

    // Testimonials
    'testimonials.badge': 'Testimonials',
    'testimonials.title': 'What our doctors say',
    'testimonials.subtitle': 'Real results from real medical practices using Bella AI voice agents.',

    // Medical Showcase
    'medical.showcase.titlePrefix': 'Our Medical Content',
    'medical.showcase.titleHighlight': 'Showcase',
    'medical.showcase.description': 'Explore our portfolio of AI-generated medical content that educates, engages, and converts healthcare professionals and patients alike.',
    'medical.showcase.hoverHint': 'Hover over each video to see our dynamic content in action',

    // Footer
    'footer.description': 'Revolutionary AI solutions transforming healthcare operations through intelligent automation and voice technology.',
    'footer.contact': 'Contact',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms of Service',
    'footer.cookies': 'Cookie Policy',
    
    // Scheduling
    'scheduling.title': 'Schedule Your Consultation',
    'scheduling.subtitle': 'Book a meeting with our team to discuss how RBI AI can transform your clinic',

    // Blog UI
    'blog.title': 'RBI AI Insights',
    'blog.subtitle': 'Where Artificial Intelligence transforms healthcare',
    'blog.searchPlaceholder': 'Search articles, topics, or authors...',
    'blog.category.all': 'All',
    'blog.category.ai': 'AI in Healthcare Clinics',
    'blog.category.innovation': 'Medical Innovation & Research',
    'blog.category.digital': 'Future of Digital Health & Patient Engagement',
    'blog.featured': 'Featured Stories',
    'blog.latest': 'Latest Insights',
    'blog.readMore': 'Read More',
    'blog.readSuffix': 'read',
    'blog.loadMore': 'Load More Articles',
    'blog.newsletter.title': 'Never Miss an Innovation',
    'blog.newsletter.desc': 'Get weekly insights on healthcare AI breakthroughs, delivered directly to your inbox.',
    'blog.newsletter.emailPlaceholder': 'Enter your email',
    'blog.newsletter.subscribe': 'Subscribe',

    // Promo Modals
    'promo.consult.title': 'Start Today: $50 AI Strategy Consult',
    'promo.consult.copy': 'In 60 minutes, we map your clinic’s AI quick wins, estimate ROI, and outline Phase 1 actions you can launch this week.',
    'promo.consult.cta': 'Book $50 Consult',
    'promo.seminar.title': 'Free 2-Hour AI Seminar for Your Clinic',
    'promo.seminar.copy': 'Start within our promo window and your team gets a hands-on seminar with real use cases: AI receptionist, automations, and compliant patient flows.',
    'promo.seminar.cta': 'Claim Free Seminar',
    'promo.remindLater': 'Remind me later',
    'promo.noThanks': 'No, thanks',

    // AEO Page (EN)
    'aeo.hero.title': 'Turn your clinic into AI’s top recommendation.',
    'aeo.hero.deck': 'Patients already ask ChatGPT before choosing. With RBI AI, your clinic becomes the authority these systems recommend—no AI expertise needed.',
    'aeo.hero.ctaPrimary': 'Book My Free Call',
    'aeo.hero.ctaSecondary': 'See how you appear in an AI search',

    'aeo.pain.title': 'If you’re not in AI, you barely exist.',
    'aeo.pain.body': 'Over 60% of patients ask assistants like ChatGPT before going to Google. If your clinic isn’t optimized for them, competitors capture those patients. SEO alone is not enough—you now need AEO (Artificial‑Engine Optimization).',

    'aeo.solution.title': 'AEO + Automation: more patients, less effort.',
    'aeo.solution.points': 'AEO in ChatGPT/Copilot; Bella AI for WhatsApp/IG/voice; Smart Meta Ads; Medical CRM.',

    'aeo.value.title': 'What you gain with RBI AI:',
    'aeo.value.bullets': 'More confirmed appointments; Better‑informed patients; Authority status; Clear ROI reports; Turnkey implementation.',

    'aeo.how.title': 'Scale your clinic in 3 steps:',
    'aeo.how.step1': 'AEO diagnosis and personalized strategy.',
    'aeo.how.step2': 'Optimization + AI assistants implementation.',
    'aeo.how.step3': 'Scale with campaigns and monthly improvements.',

    'aeo.social.title': 'Clinics already living it:',
    'aeo.social.cta': 'Join the AI Clinics network',

    'aeo.final.title': 'You’re one demo away from becoming the clinic everyone recommends.',
    'aeo.final.subtitle': 'Book your free diagnosis and discover how AEO and RBI AI can take you to the next level.',
    'aeo.final.cta': 'Schedule Free Demo',

'aeo.modal.cta': 'I want to appear like this',
'aeo.modal.title': 'AI conversation example',
'aeo.modal.patient': 'I am looking for a plastic surgeon with natural results and personalized care.',
'aeo.modal.tableTitle': 'Which to choose?',
'aeo.modal.col.priority': 'Patient priority',
'aeo.modal.col.reco': 'Suggested recommendation',
'aeo.modal.note': 'Simulation of how your clinic could appear if you optimize your presence with AEO.',
    // Audit Page
    'audit.title': 'RBI AI – Medical Clinic Diagnostic Audit (Interactive)',
    'audit.desc': 'In 5–7 minutes, see where your clinic stands, get a diagnostic, pricing, and next steps.',
    'audit.next': 'Next',
    'audit.back': 'Back',
    'audit.select': 'Select',
    'audit.selected': 'Selected',
    'audit.reviewSummary': 'Review Summary',
    'audit.seeAIPreview': 'See AI Preview',
    'audit.sidebar.score': 'AEO Score',
    'audit.sidebar.pricing': 'Pricing',
    'audit.topRecommendations': 'Top recommendations',
    'audit.view': 'View',
    'audit.monthlyTotal': 'Monthly Total',

    // Audit Visual
    'auditVisual.title': 'Interactive Visual Audit – AEO Score & Plan',
    'auditVisual.desc': 'Answer a few questions to see your score and tailored recommendations.',
    'auditVisual.h1': 'Interactive Visual Audit',
    'auditVisual.p': 'Answer the cards below. Your score and recommendations update live.',
    'auditVisual.generatePDF': 'Download PDF Report',
    'auditVisual.goToQuote': 'Go to Quote',

    // Audit Start (EN)
    'auditStart.title': 'Start Interactive Audit – Clinics AEO',
    'auditStart.desc': 'Begin the interactive audit with the first question cards now.',

    // Questions
    'audit.q.doctors': 'Number of doctors',
    'audit.q.aiChecked': 'AI maturity',
    'audit.q.ai.good': 'Good',
    'audit.q.ai.bad': 'Needs work',
    'audit.q.ai.never': 'Never checked',
    'audit.q.paidAds': 'Paid ads running',
    'audit.q.ads.meta': 'Meta',
    'audit.q.ads.google': 'Google',
    'audit.q.booking': 'Booking method',
    'audit.q.booking.manual': 'Manual',
    'audit.q.booking.online': 'Online',
    'audit.q.booking.ai': 'AI Assistant',
    'audit.q.channels': 'Active channels',
    'audit.q.chan.whatsapp': 'WhatsApp',
    'audit.q.chan.instagram': 'Instagram',
    'audit.q.chan.email': 'Email',
    'audit.q.chan.phone': 'Phone',
    'audit.q.crm': 'Do you use a CRM?',
    'audit.q.bottleneck': 'Main bottleneck',
    'audit.q.bottleneck.not_enough_leads': 'Not enough leads',
    'audit.q.bottleneck.low_conversion': 'Low conversion',
    'audit.q.bottleneck.no_shows': 'No-shows',
    'audit.q.bottleneck.high_costs': 'High costs',
    'audit.q.bottleneck.follow_up': 'Follow-up',
    'audit.q.bottleneck.other': 'Other',
    'audit.q.goals': 'Goals',
    'audit.q.goals.international': 'International/Tourism',
    'audit.q.goals.automation': 'Automation',
    'audit.q.goals.scale': 'Scale',
    'audit.q.goals.growth': 'Growth',
    'audit.q.services': 'Services',
    'audit.q.services.plastic_surgery': 'Plastic surgery',
    'audit.q.services.dentistry': 'Dentistry',
    'audit.q.services.dermatology': 'Dermatology',
    'audit.q.services.aesthetics': 'Aesthetics',
    'audit.q.services.fertility': 'Fertility',
    'audit.q.services.general': 'General',
    'audit.q.startWhen': 'When do you want to start?',
    'audit.q.start.now': 'Right away',
    
    // Services
    'services.medicine.title': 'Medicine',
    'services.medicine.subtitle': 'Services',
    'services.medicine.description': 'General medicine, primary care, and medical treatments',
    'services.dental.title': 'Dental',
    'services.dental.subtitle': 'Services', 
    'services.dental.description': 'Dental procedures, oral health treatments, and dental care',
    'services.aesthetic.title': 'Aesthetic',
    'services.aesthetic.subtitle': 'Services',
    'services.aesthetic.description': 'Cosmetic procedures, beauty treatments, and aesthetic medicine',
    'audit.q.start.3m': 'Within 3 months',
    'audit.q.start.6-12m': 'In 6–12 months',
    'audit.q.start.exploring': 'Just exploring',

    // Common
    'common.home': 'Home',
    'common.yes': 'Yes',
    'common.no': 'No',

    // Step Hero (EN)
    'page.index.stepHero.badge': 'Quick start',
    'page.index.stepHero.title': 'Get Your AI Digital Assessment in 4 Steps',
    'page.index.stepHero.subtitle': 'From assessment to implementation — discover your growth potential.',
    'page.index.stepHero.primaryCta': 'Book a Call',
    'page.index.stepHero.primaryCtaAria': 'Book a consultation call',
    'page.index.stepHero.secondaryCta': 'See demo',
    'page.index.stepHero.secondaryCtaAria': 'Jump to demo',
    'page.index.stepHero.stepsAria': 'Assessment process steps',
    'page.index.stepHero.steps.1.title': 'Complete Clinic Assessment Form',
    'page.index.stepHero.steps.1.desc': 'Fill out our comprehensive digital presence questionnaire',
    'page.index.stepHero.steps.1.cta': 'Start Assessment',
    'page.index.stepHero.steps.2.title': 'Receive AI Assessment Clinic Report',
    'page.index.stepHero.steps.2.desc': 'Get your detailed analysis with actionable insights',
    'page.index.stepHero.steps.2.cta': 'View Sample Report',
    'page.index.stepHero.steps.3.title': 'Schedule 1-1 Call',
    'page.index.stepHero.steps.3.desc': 'Walk through every step with our expert team',
    'page.index.stepHero.steps.3.cta': 'Book a Call',
    'page.index.stepHero.steps.4.title': 'Launch & Grow',
    'page.index.stepHero.steps.4.desc': 'Implement your custom strategy and scale your practice',
    'page.index.stepHero.steps.4.cta': 'Get Started',
    
    // Audit Report translations (EN)
    'audit.report.title': 'Your AI Practice Diagnostic Report',
    'audit.report.complete': 'Diagnostic Complete',
    'audit.report.downloadPDF': 'Download PDF',
    'audit.report.aeoScore': 'AEO Score',
    'audit.report.recommendations': 'Top Recommendations',
    
    // Interactive Audit Steps translations (EN)
    'audit.steps.practiceSize.title': 'Practice Size',
    'audit.steps.practiceSize.description': 'How many doctors work at your practice?',
    'audit.steps.practiceSize.options.small': '1-2 Doctors',
    'audit.steps.practiceSize.options.smallDesc': 'Small practice',
    'audit.steps.practiceSize.options.medium': '3-5 Doctors',
    'audit.steps.practiceSize.options.mediumDesc': 'Medium practice',
    'audit.steps.practiceSize.options.large': '6+ Doctors',
    'audit.steps.practiceSize.options.largeDesc': 'Large practice',
    
    'audit.steps.challenges.title': 'Current Challenges',
    'audit.steps.challenges.description': 'What is your biggest challenge right now?',
    'audit.steps.challenges.options.acquisition': 'Patient Acquisition',
    'audit.steps.challenges.options.acquisitionDesc': 'Getting new patients',
    'audit.steps.challenges.options.management': 'Appointment Management',
    'audit.steps.challenges.options.managementDesc': 'Scheduling and bookings',
    'audit.steps.challenges.options.retention': 'Patient Retention',
    'audit.steps.challenges.options.retentionDesc': 'Keeping existing patients',
    'audit.steps.challenges.options.roi': 'Marketing ROI',
    'audit.steps.challenges.options.roiDesc': 'Ineffective marketing spend',
    
    'audit.steps.experience.title': 'AI Experience',
    'audit.steps.experience.description': 'How familiar are you with AI tools in healthcare?',
    'audit.steps.experience.options.none': 'No Experience',
    'audit.steps.experience.options.noneDesc': 'Never used AI tools',
    'audit.steps.experience.options.basic': 'Basic Knowledge',
    'audit.steps.experience.options.basicDesc': 'Some awareness of AI',
    'audit.steps.experience.options.experienced': 'Experienced',
    'audit.steps.experience.options.experiencedDesc': 'Currently using AI tools',
    
    // Aviation page
    'pages.aviation.seo.title': 'Aviation - RBI AI Pilot Academy',
    'pages.aviation.seo.description': 'AI-powered aviation training and pilot education platform',
  },
  es: {
    // Hero Section
    'hero.title': 'Inteligencia Artificial en tu Clínica Médica',
    'hero.subtitle': 'Revolución',
    'hero.description': 'Acabas de encontrar al socio adecuado para la adopción de IA en tu clínica. Lo probamos en nuestro laboratorio—tú adoptas lo que funciona. Empieza hoy.',
    'hero.explore': 'Reservar Llamada',
    'hero.learnMore': 'Chatear con IA',

    // AI Digital Clinic Assessment Hero
    'hero.assessment.title': 'Evaluación Digital de Clínica con IA',
    'hero.assessment.subtitle': 'Transforme Su Práctica Médica con Análisis Basados en Datos',
    'hero.assessment.description': 'Obtenga una auditoría gratuita del ecosistema digital completo de su clínica y estrategias impulsadas por IA para hacer crecer su práctica.',
    
    // AI Voice Agent Page
    'aiVoiceAgent.page.title': 'Agente de Voz IA - Bella Recepcionista de Voz | RBI AI',
    'aiVoiceAgent.page.description': 'Conozca a Bella, su recepcionista de voz con IA. Maneje llamadas de pacientes 24/7, programe citas, responda preguntas y brinde un servicio excepcional sin intervención humana.',
    'aiVoiceAgent.hero.title': 'Conozca a Bella: Su Recepcionista de Voz IA',
    'aiVoiceAgent.hero.subtitle': 'Deje que la IA maneje sus llamadas telefónicas mientras su equipo se enfoca en el cuidado del paciente. Bella responde preguntas, reserva citas y proporciona información—todo con una voz natural y humana.',
    
    // Conversational AI Page (ES)
    'conversationalAI.page.title': 'IA Conversacional - Agentes WhatsApp e Instagram | RBI AI',
    'conversationalAI.page.description': 'Agentes conversacionales de IA para WhatsApp e Instagram. Automatice la comunicación con pacientes, reservas de citas y seguimientos 24/7 con automatización inteligente de chat.',
      'conversationalAI.hero.title': 'Agentes de IA para WhatsApp e Instagram',
      'conversationalAI.hero.subtitle': 'Automatice conversaciones con pacientes y reservas de citas en sus plataformas de mensajería más utilizadas. Deje que la IA maneje consultas, programe citas y comprometa pacientes 24/7—mientras su equipo se enfoca en el cuidado.',
      
    // Meta Ads Page (ES)
    'metaAds.page.title': 'Gestión Meta Ads - Publicidad Google e Instagram | RBI AI',
    'metaAds.page.description': 'Gestión experta de publicidad en Google Ads e Instagram para clínicas. Seguimiento de campañas en tiempo real, optimización y estrategias enfocadas en ROI para hacer crecer su base de pacientes.',
    'metaAds.hero.title': 'Publicidad Basada en Datos Que Atrae Pacientes',
    'metaAds.hero.subtitle': 'Gestión profesional de sus campañas publicitarias en Google e Instagram. Optimizamos cada dólar para maximizar la adquisición de pacientes, rastreamos el rendimiento en tiempo real y entregamos ROI medible para su práctica.',
    
    // AI Audit Page (ES)
    'aiAudit.page.title': 'Auditoría AI - Análisis Integral de Práctica Médica | RBI AI',
    'aiAudit.page.description': 'Obtenga una auditoría completa impulsada por IA de su práctica médica. Benchmarking de competidores, análisis de automatización, oportunidades de integración AI y optimización de presencia digital.',
    'aiAudit.hero.title': 'Descubra Dónde la IA Puede Transformar Su Práctica',
    'aiAudit.hero.subtitle': 'Análisis integral del rendimiento de su clínica, potencial de automatización y oportunidades de crecimiento. Obtenga insights accionables sobre benchmarking de competidores, integración AI, flujos de trabajo y presencia digital—todo impulsado por análisis avanzado.',
    
    // Rotating Headlines
    'hero.rotating.line1': 'Evaluación Digital con IA',
    'hero.rotating.line2': 'Clínicas de Células Madre',
    'hero.rotating.line3': 'Cirujanos Plásticos',
    'hero.rotating.line4': 'Dentistas',
    'hero.rotating.line5': 'Impulsado por IA',


    // Hero Video Section (ES)
    'heroVideo.title1': 'Realidad Aumentada',
    'heroVideo.title2': 'Experiencia',
    'heroVideo.description': 'No se trata solo de vivir más, sino de vivir mejor. Con IA aplicada a la medicina y la longevidad, transformamos datos en decisiones y tecnología en años de vida saludable. <strong>Descubre nuestra experiencia de realidad aumentada</strong> y explore cómo RBI AI te conecta con soluciones que hoy construyen el mañana.',
    'heroVideo.arButton': 'Ver Experiencia AR',
    'heroVideo.qrText': 'Escanea con tu móvil para ver la experiencia AR',

    // Dental (ES)
    'dental.hero.title': 'IA que llena tu agenda dental',
    'dental.hero.subtitle': 'De interés a pacientes agendados — automatizado.',
    'dental.hero.description': 'Aplicamos contenido, agentes y automatizaciones de IA adaptados a clínicas dentales.',
    'dental.hero.ctaPrimary': 'Reservar llamada de IA Dental',
    'dental.hero.ctaSecondary': 'Solicitar cotización dental',
    'dental.hero.videoTitle': 'Video demo de IA Dental',

    // Imágenes del planificador de turismo dental (ES)
    'dentalTourism.images.step1': 'Clínica dental en Costa Rica — paciente o acompañante',
    'dentalTourism.images.step2': 'Consulta de procedimientos dentales en Costa Rica',
    'dentalTourism.images.step3': 'Viaje dental a Costa Rica — planificación de fechas y presupuesto',
    'dentalTourism.images.step4': 'Básicos del viaje — vuelos a San José, Costa Rica',
    'dentalTourism.images.step5': 'Hospedaje y tours en Costa Rica para el viaje dental',
    'dentalTourism.images.step6': 'Soporte de concierge para turismo dental en Costa Rica',
    'dentalTourism.images.step7': 'Resumen del viaje dental — itinerario en Costa Rica',


// Titulares rotativos (ES)
    'hero.rotating.1': 'Encontraste<br />el socio de<br />IA correcto<br />para tu clínica',
    'hero.rotating.2': 'El viaje de<br />IA de tu<br />clínica<br />empieza hoy',
    'hero.rotating.3': 'Conviértete<br />en una<br />clínica<br />impulsada por IA',
    'hero.rotating.4': 'Destaca con<br />marketing<br />primero‑IA que<br />se adapta a diario',
    'hero.rotating.5': 'Conviértete<br />en la<br />autoridad de<br />ChatGPT en tu zona',
    'hero.rotating.6': 'Especialistas<br />en IA en el<br />mundo creando<br />para tu industria',
    'hero.rotating.7': 'Lo probamos<br />en el<br />laboratorio<br />— tú adoptas lo que funciona',
    'hero.rotating.8': 'Entrena una<br />recepcionista<br />IA que agenda<br />pacientes 24/7',
    'hero.rotating.9': 'Ejemplos reales, resultados reales — no “IA” genérica',
    'hero.rotating.10': 'Construimos<br />el futuro<br />para quienes<br />quieren evolucionar',
    'hero.rotating.11': 'Nuestro<br />laboratorio<br />construye para hospitales;<br />las PyMEs escalan con RBI AI',
    'hero.rotating.12': 'Únete a la<br />comunidad de<br />IA médica y aprende<br />con casos reales',
    // Stats Section
    'stats.title': 'La Estrategia Que Realmente Funciona',
    'stats.professionals': 'Profesionales de la salud alcanzados a través de nuestra estrategia de contenido impulsada por IA en los últimos 6 meses',
    'stats.increase': 'aumento en leads calificados para nuestros clientes de innovación en salud. Nuestro contenido no solo involucra—convierte.',
    'stats.days': 'el tiempo promedio que toma nuestro contenido optimizado por IA para comenzar a generar leads calificados de la industria médica',
    'stats.getStarted': 'COMENZAR AHORA',
    
    // Success Metrics
    'metrics.title': 'Rastrea el Crecimiento de tu Práctica con',
    'metrics.subtitle': 'Análisis Avanzados',
    'metrics.description': 'Monitorea la adquisición de pacientes, el crecimiento de ingresos y la eficiencia operacional con insights en tiempo real impulsados por análisis con IA.',
    
    // Opportunities
    'opportunities.title': 'Transforma Leads de Pacientes en',
    'opportunities.subtitle': 'Oportunidades de Ingresos',
    'opportunities.description': 'Gestiona tu pipeline de pacientes con puntuación inteligente de leads, seguimientos automatizados y seguimiento de conversiones. Nunca pierdas un paciente potencial.',
    
    // Automated Workflows
    'workflows.title': 'Automatiza Cada Proceso con',
    'workflows.subtitle': 'Flujos de Trabajo Inteligentes',
    'workflows.description': 'Optimiza facturación, gestión de consentimientos, seguimientos de pacientes y recolección de reseñas de Google. Reduce el trabajo manual en un 90% mientras aseguras que nada se escape.',
    
    // Pricing
    'pricing.title': 'PLANES DE PAGO',
    'pricing.choosePlan': 'Elige Tu Plan',
'pricing.subtitle': 'Selecciona el número de doctores de tu clínica para estimaciones de precio exactas.',
    'pricing.popular': 'Más Popular',
    'pricing.implementation': 'Implementación x Dr:',
    'pricing.month': '/mes',
    'pricing.implementedBy': 'Implementado por',
    'pricing.base': 'Base:',
    'pricing.addons': 'Add-ons:',
    'pricing.startWith': 'Comenzar con',
    'pricing.addonsTitle': 'Add-ons:',
    'pricing.add': 'Agregar',
    'pricing.selected': 'Plan Seleccionado:',
    'pricing.basePlan': 'Plan Base:',
    'pricing.addedAddons': 'Add-ons Agregados:',
    'pricing.monthlyTotal': 'Total Mensual:',
    'pricing.disclaimer1': '*Cada dr. adicional tendría una subcuenta de Zapiy Ai. La implementación incluye el entrenamiento del Asistente y la configuración de los flujos.',
    'pricing.perDoctorDisclaimer': '*Los precios son por doctor',
    'pricing.implementationFee': 'Costo de Implementación:',
    'pricing.disclaimer2': '** Total disponible para todos los Drs - La clínica puede aumentar el presupuesto cuando lo desee',
    'pricing.remove': 'Remover',
    'pricing.selectPlanFirst': 'Por favor selecciona un plan primero',
    'pricing.requestSent': '¡Solicitud Enviada Exitosamente!',
    'pricing.requestDescription': 'Nos pondremos en contacto contigo en menos de 24 horas.',
    'pricing.sendError': 'Error al Enviar',
    'pricing.sendErrorDescription': 'Por favor inténtalo nuevamente o contáctanos directamente.',
    'pricing.contactInfo': 'Información de Contacto',
    'pricing.fullName': 'Nombre Completo',
    'pricing.fullNamePlaceholder': 'Ingrese su nombre completo',
    'pricing.email': 'Correo Electrónico',
    'pricing.emailPlaceholder': 'Ingrese su correo electrónico',
    'pricing.clinicName': 'Nombre de la Clínica',
    'pricing.clinicNamePlaceholder': 'Ingrese el nombre de su clínica',
    'pricing.phoneNumber': 'Número de Teléfono',
    'pricing.phoneNumberPlaceholder': 'Ingrese su número de teléfono',
    'pricing.additionalMessage': 'Mensaje Adicional',
    'pricing.additionalMessagePlaceholder': '¿Algún requerimiento especial o pregunta adicional?',
    'pricing.sendRequest': 'Enviar Solicitud a RBI AI',
    
    // Plans
    'plans.basic.name': 'BÁSICO',
    'plans.basic.package': 'Paquete 1',
    'plans.basic.subtitle': 'Automatización + AI Assistant',
    'plans.basic.description': 'IA Assistant + Automatización base',
    'plans.professional.name': 'PROFESIONAL',
    'plans.professional.package': 'Paquete 2',
    'plans.professional.subtitle': 'Meta Ads + Piezas Gráficas',
    'plans.professional.description': 'Paquete 1 + Meta Ads + Gráficos',
    'plans.premium.name': 'PREMIUM',
    'plans.premium.package': 'Paquete 3',
    'plans.premium.subtitle': 'Google Ads + Piezas Gráficas',
    'plans.premium.description': 'Paquete 2 + Google Ads + SEO',
    'plans.included': 'Incluido',
    
    // Features
    'features.aiAssistant': 'Incluye Asistente IA WhatsApp + IG',
    'features.automations': 'Automatizaciones WhatsApp & Email',
    'features.googleReviews': 'Google Reviews automáticos',
    'features.technicalSupport': 'Soporte técnico Zapiy',
    'features.metaAds': 'Campañas Meta Ads',
    'features.monthlyMeeting': '1 Reunión de Reporte Mensual',
    'features.aiSpecialistSupport': 'Soporte de Especialista en IA',
    
    'features.graphicPieces': '6 Piezas gráficas mensuales',
    'features.metaCrmAi': 'Meta – CRM – AI',
    'features.metaDashboard': 'Dashboard rendimiento (Meta)',
    'features.googleAds': 'Campañas Google Ads',
    
    'features.ga4Tracking': 'GA4 + trazabilidad avanzada',
    'features.freeSeminar': 'Seminario Gratuito de IA',
    
    // Add-ons
    'addons.webSupport': 'Soporte Web institucional',
    'addons.webSupportPrice': '$350/mes (por clínica)',
    'addons.bellaVoice': 'Bella Ai Voice Agent',
    'addons.bellaVoicePrice': '$295/mes por doctor',
    'addons.whatsappBlast': 'WhatsApp Blast',
    'addons.whatsappBlastPrice': '$97/mes por doctor',
    
    // FAQ
    'faq.title': 'Preguntas Frecuentes',
    'faq.subtitle': 'Obtén respuestas a preguntas comunes sobre la implementación y beneficios de Bella AI',
    'faq.stillQuestions': '¿Aún tienes preguntas?',
    'faq.teamReady': 'Nuestro equipo está listo para responder cualquier pregunta específica sobre las necesidades de tu clínica.',
    'faq.schedule': 'Programar una Consulta',
    
    // Audit FAQ
    'faq.audit.title': 'Preguntas Frecuentes del Proceso de Auditoría',
    'faq.audit.subtitle': 'Obtén respuestas sobre nuestro proceso de Evaluación Digital de Clínica con IA',
    'faq.audit.q1': '¿Cuánto tiempo tarda en recibir mi evaluación?',
    'faq.audit.a1': 'Recibirás tu reporte completo de auditoría con IA dentro de 72 horas después de completar el formulario de evaluación. Nuestro equipo analiza la presencia digital de tu clínica, oportunidades de IA y potencial de crecimiento para entregar información accionable.',
    'faq.audit.q2': '¿Necesito completar un formulario?',
    'faq.audit.a2': 'Sí, deberás completar nuestro formulario de evaluación digital. Esto nos ayuda a entender la configuración actual de tu clínica, canales de marketing, sistemas de reserva y preparación para IA. El formulario toma alrededor de 10-15 minutos en completarse.',
    'faq.audit.q3': '¿Alguien me explicará los resultados paso a paso?',
    'faq.audit.a3': '¡Absolutamente! Después de que recibas tu reporte de auditoría, programamos una llamada de consulta donde nuestros especialistas en IA te explican los hallazgos paso a paso. Explicaremos oportunidades, priorizaremos recomendaciones y responderemos todas tus preguntas.',
    'faq.audit.q4': '¿Qué incluye la auditoría?',
    'faq.audit.a4': 'La auditoría cubre 10 áreas principales: comparación con competidores, analítica en tiempo real, gestión de reputación, ROI de campañas, oportunidades de integración de IA, análisis del recorrido del paciente, flujos de trabajo automatizados, presencia digital, optimización SEO y seguimiento de llamadas.',
    'faq.audit.q5': '¿La auditoría es realmente gratis?',
    'faq.audit.a5': 'Sí, la Evaluación Digital de Clínica con IA es completamente gratuita. No hay obligación de comprar ningún servicio. Proporcionamos la auditoría para ayudar a las clínicas a comprender sus oportunidades de IA.',
    'faq.audit.q6': '¿Los datos de mi clínica están seguros?',
    'faq.audit.a6': 'Absolutamente. Todos los datos compartidos durante la auditoría están encriptados, almacenados de forma segura y se utilizan solo para tu evaluación. Seguimos protocolos estrictos de privacidad y nunca compartimos tu información con terceros.',
    'faq.audit.q7': '¿Qué sucede después de la auditoría?',
    'faq.audit.a7': 'Después de la llamada de consulta, recibirás un plan de acción detallado. Puedes elegir implementar las recomendaciones tú mismo, trabajar con tu equipo existente o asociarte con nosotros. No hay presión: los conocimientos son tuyos para usarlos como prefieras.',
    
    // Bella AI Voice Agent
    'bella.title': 'Prueba el Agente de Voz Bella AI',
    'bella.subtitle': 'Experimenta cómo Bella AI puede transformar la comunicación de tu clínica',
    'bella.name': 'Bella - AI Receptionist',
    'bella.status': 'En línea 24/7',
    'bella.greeting': '¡Hola! Soy Bella, tu asistente de IA. ¿En qué puedo ayudarte hoy?',
    'bella.talkButton': 'Hablar con Bella',
    'bella.listening': 'Escuchando...',
    'bella.scheduleDemo': 'RESERVAR LLAMADA',
    'bella.scheduleTitle': 'Reserva tu Llamada con Bella AI',
    'bella.scheduleSubtitle': 'Elige el horario que mejor te funcione',
    'bella.try': 'EMPIEZA AQUÍ',

    // Compliance Section (ES)
    'compliance.title': 'Seguridad y Cumplimiento de Nivel Empresarial',
    'compliance.subtitle': 'Los datos de sus pacientes están protegidos por estándares de seguridad líderes en la industria y cumplimiento regulatorio completo para organizaciones de salud.',
    'compliance.hipaa.title': 'Cumplimiento HIPAA',
    'compliance.hipaa.description': 'Diseñado para proveedores de salud de EE.UU. con protección completa de PHI y Acuerdos de Asociado Comercial.',
    'compliance.hipaa.feature1': 'Acuerdos de Asociado Comercial (BAAs) disponibles',
    'compliance.hipaa.feature2': 'Cifrado de extremo a extremo (TLS 1.2+, AES-256)',
    'compliance.hipaa.feature3': 'Registros de auditoría y controles de acceso completos',
    'compliance.gdpr.title': 'Cumplimiento GDPR',
    'compliance.gdpr.description': 'Cumplimiento total con las regulaciones europeas de protección de datos, garantizando los derechos de privacidad del paciente.',
    'compliance.gdpr.feature1': 'Derecho de acceso, rectificación y eliminación',
    'compliance.gdpr.feature2': 'Gestión de consentimiento y portabilidad de datos',
    'compliance.gdpr.feature3': 'Opciones de residencia de datos en la UE disponibles',
    'compliance.learnMore': 'Más Información',
    'compliance.trustMessage': 'Los datos de sus pacientes están seguros con nosotros',
    'compliance.certifications': 'SOC 2 Tipo II • ISO 27001 • Listo para GDPR',

    // AI Conversation Hero
    'aiHero.title': 'Agentes Conversacionales de IA para Agendar Pacientes',
    'aiHero.description': 'Después de captar leads con nuestro contenido médico atractivo, nuestros agentes conversacionales de IA se encargan de nutrir y convertir esos prospectos en citas reservadas a través de WhatsApp e Instagram.',
    'aiHero.whatsapp.caption': 'Agendamiento instantáneo por WhatsApp con flujo conversacional inteligente',
    'aiHero.instagram.caption': 'Interacción natural en Instagram con integración de reservas',
    'aiHero.features.instant.title': 'Respuesta Instantánea',
    'aiHero.features.instant.desc': 'Nuestros agentes de IA responden en segundos en WhatsApp e Instagram, asegurando que ningún paciente potencial quede esperando. Disponibles 24/7.',
    'aiHero.features.qualify.title': 'Calificación Inteligente',
    'aiHero.features.qualify.desc': 'Proceso avanzado que entiende necesidades, síntomas y seguros del paciente para dirigirlo al profesional correcto.',
    'aiHero.features.booking.title': 'Reserva sin Fricción',
    'aiHero.features.booking.desc': 'Integración directa con sistemas de gestión para agendar en tiempo real, confirmar y enviar recordatorios automáticos.',
    'aiHero.features.hipaa.title': 'Cumplimiento HIPAA',
    'aiHero.features.hipaa.desc': 'Todas las conversaciones están cifradas y cumplen HIPAA, garantizando privacidad y seguridad de los datos.',
    'aiHero.cta.title': '¿Listo para automatizar tus reservas de pacientes?',
    'aiHero.cta.desc': 'Nuestros agentes conversacionales de IA han ayudado a incrementar las reservas en un 340% mientras reducen el esfuerzo administrativo en un 60%.',
    'aiHero.stats.conversion': 'Tasa de conversión',
    'aiHero.stats.responseTime': 'Tiempo de respuesta promedio',
    'aiHero.stats.availability': 'Disponibilidad',

    // ROI Calculator
    'roi.title': '¿Aún te preocupa invertir en IA?',
    'roi.subtitle': 'Descubre cuántos pacientes necesitas para recuperar tu inversión y generar utilidades',
    'roi.cardTitle': 'Datos de tu clínica',
    'roi.avgConsultation': 'Costo promedio de consulta (USD)',
    'roi.avgConsultation.help': 'Incluye consultas, procedimientos y servicios promedio',
    'roi.selectPackage': 'Selecciona tu paquete',
    'roi.choosePlaceholder': 'Elige un paquete',
    'roi.implementation': 'Implementación',
    'roi.calculate': 'Calcular ROI',
    'roi.clear': 'Limpiar',
    'roi.results.title': 'Resultados del análisis',
    'roi.results.patientsToRecover': 'Pacientes para recuperar la inversión inicial',
    'roi.results.monthlyPatients': 'Pacientes mensuales para cubrir costos',
    'roi.results.potentialRevenue': 'Ingresos mensuales potenciales:',
    'roi.results.netProfit': 'Utilidad neta mensual:',
    'roi.results.monthlyROI': 'ROI mensual:',
    'roi.results.breakEven': 'Punto de equilibrio',
    'roi.results.breakEven.prefix': 'Recuperarás tu inversión inicial en aproximadamente ',
    'roi.results.breakEven.suffix': ' días hábiles (asumiendo 8 pacientes por día)',
    'roi.results.summary': 'Resumen ejecutivo:',
    'roi.empty': 'Completa los datos para ver tu análisis de ROI personalizado',
    'roi.alertFillFields': 'Por favor completa todos los campos',

    // Testimonials
    'testimonials.badge': 'Testimonios',
    'testimonials.title': 'Lo que dicen nuestros doctores',
    'testimonials.subtitle': 'Resultados reales de prácticas médicas que usan los agentes de voz Bella AI.',

    // Medical Showcase
    'medical.showcase.titlePrefix': 'Nuestro Contenido Médico',
    'medical.showcase.titleHighlight': 'Destacado',
    'medical.showcase.description': 'Explora nuestro portafolio de contenido médico generado con IA que educa, cautiva y convierte a profesionales y pacientes.',
    'medical.showcase.hoverHint': 'Pasa el cursor sobre cada video para ver nuestro contenido en acción',

    // Footer
    'footer.description': 'Soluciones de IA que transforman la operación en salud mediante automatización inteligente y tecnología de voz.',
    'footer.contact': 'Contacto',
    'footer.privacy': 'Política de Privacidad',
    'footer.terms': 'Términos del Servicio',
    'footer.cookies': 'Política de Cookies',
    
    // Scheduling
    'scheduling.title': 'Agenda tu Consulta',
    'scheduling.subtitle': 'Reserva una reunión con nuestro equipo para discutir cómo RBI AI puede transformar tu clínica',

    // Blog UI
    'blog.title': 'RBI AI Insights',
    'blog.subtitle': 'Donde la Inteligencia Artificial transforma la salud',
    'blog.searchPlaceholder': 'Busca artículos, temas o autores...',
    'blog.category.all': 'Todos',
    'blog.category.ai': 'IA en Clínicas de Salud',
    'blog.category.innovation': 'Innovación Médica e Investigación',
    'blog.category.digital': 'Futuro de la Salud Digital y la Experiencia del Paciente',
    'blog.featured': 'Historias Destacadas',
    'blog.latest': 'Últimos Análisis',
    'blog.readMore': 'Leer Más',
    'blog.readSuffix': 'de lectura',
    'blog.loadMore': 'Cargar más artículos',
    'blog.newsletter.title': 'No te pierdas ninguna innovación',
    'blog.newsletter.desc': 'Recibe semanalmente avances en IA para salud, directo a tu correo.',
    'blog.newsletter.emailPlaceholder': 'Ingresa tu email',
    'blog.newsletter.subscribe': 'Suscribirse',

    // Promociones (ES)
    'promo.consult.title': 'Empieza Hoy: Consultoría de IA por $50',
    'promo.consult.copy': 'En 60 minutos, definimos quick wins, ROI y un plan de Fase 1 que puedes activar esta semana.',
    'promo.consult.cta': 'Reservar Consultoría por $50',
    'promo.seminar.title': 'Seminario de IA de 2 Horas Gratis',
    'promo.seminar.copy': 'Si empiezas dentro del periodo promocional, tu equipo recibe un taller práctico con casos reales: recepcionista IA, automatizaciones y flujos compliant.',
    'promo.seminar.cta': 'Solicitar Seminario Gratis',
    'promo.remindLater': 'Recuérdame luego',
    'promo.noThanks': 'No, gracias',

    // Página AEO (ES)
    'aeo.hero.title': 'Convierte tu clínica en la primera recomendación de la IA.',
    'aeo.hero.deck': 'Los pacientes ya buscan en ChatGPT y otros asistentes antes de decidir. Con RBI AI, tu clínica se vuelve la autoridad que estos sistemas recomiendan, sin que tengas que aprender sobre IA.',
    'aeo.hero.ctaPrimary': 'Reservar mi Llamada Gratis',
    'aeo.hero.ctaSecondary': 'Ver cómo apareces en una búsqueda AI',

    'aeo.pain.title': 'Si tu clínica no aparece en IA, es como si no existiera.',
    'aeo.pain.body': 'Hoy, más del 60% de los pacientes buscan respuestas médicas en asistentes como ChatGPT antes de entrar a Google. Si tu clínica no está optimizada para estos motores, tus competidores ya están captando esas oportunidades. SEO por sí solo ya no es suficiente. Ahora, necesitas AEO – Artificial-Engine Optimization.',

    'aeo.solution.title': 'AEO + Automatización: más pacientes, menos esfuerzo.',
    'aeo.solution.points': 'AEO en ChatGPT/Copilot; Bella AI en WhatsApp/IG/voz; Meta Ads inteligentes; CRM médico.',

    'aeo.value.title': 'Lo que ganas con RBI AI:',
    'aeo.value.bullets': 'Más citas confirmadas; Pacientes mejor informados; Autoridad en tu especialidad; Reportes claros de ROI; Implementación llave en mano.',

    'aeo.how.title': 'Así escalamos tu clínica en 3 pasos:',
    'aeo.how.step1': 'Diagnóstico AEO y estrategia personalizada.',
    'aeo.how.step2': 'Implementación de optimización y asistentes IA.',
    'aeo.how.step3': 'Escalamos con campañas y mejoras mensuales.',

    'aeo.social.title': 'Clínicas que ya lo viven:',
    'aeo.social.cta': 'Quiero unirme a la red de Clínicas AI',

    'aeo.final.title': 'Estás a una llamada de convertirte en la clínica que todos recomiendan.',
    'aeo.final.subtitle': 'Reserva tu diagnóstico gratuito y descubre cómo AEO y RBI AI pueden llevarte a tu siguiente nivel.',
    'aeo.final.cta': 'Reservar Llamada Gratis',

    'aeo.modal.cta': 'Quiero aparecer así',

    // Página de Auditoría
    'audit.title': 'RBI AI – Auditoría Diagnóstica para Clínicas (Interactiva)',
    'audit.desc': 'En 5–7 minutos sabrás dónde está tu clínica, con diagnóstico, precios y siguientes pasos.',
    'audit.next': 'Siguiente',
    'audit.back': 'Atrás',
    'audit.select': 'Seleccionar',
    'audit.selected': 'Seleccionado',
    'audit.reviewSummary': 'Ver Resumen',
    'audit.seeAIPreview': 'Ver vista previa de IA',
    'audit.sidebar.score': 'Puntaje AEO',
    'audit.sidebar.pricing': 'Precios',
    'audit.topRecommendations': 'Principales recomendaciones',
    'audit.view': 'Ver',
    'audit.monthlyTotal': 'Total Mensual',

    // Audit Visual
    'auditVisual.title': 'Auditoría Visual Interactiva – Puntaje AEO y Plan',
    'auditVisual.desc': 'Responde algunas preguntas para ver tu puntaje y recomendaciones.',
    'auditVisual.h1': 'Auditoría Visual Interactiva',
    'auditVisual.p': 'Responde las tarjetas abajo. Tu puntaje y recomendaciones se actualizan en vivo.',
    'auditVisual.generatePDF': 'Descargar informe PDF',
    'auditVisual.goToQuote': 'Ir a Cotización',

    // Audit Start (ES)
    'auditStart.title': 'Iniciar Auditoría Interactiva – AEO Clínicas',
    'auditStart.desc': 'Empieza la auditoría interactiva con las primeras tarjetas ahora.',

    // Preguntas
    'audit.q.services': 'Servicios',
    'audit.q.services.plastic_surgery': 'Cirugía plástica',
    'audit.q.services.dentistry': 'Odontología',
    'audit.q.services.dermatology': 'Dermatología',
    'audit.q.services.aesthetics': 'Estética',
    'audit.q.services.fertility': 'Fertilidad',
    'audit.q.services.general': 'General',
    
    // Services (ES)
    'services.medicine.title': 'Medicina',
    'services.medicine.subtitle': 'Servicios',
    'services.medicine.description': 'Medicina general, atención primaria, y tratamientos médicos',
    'services.dental.title': 'Dental',
    'services.dental.subtitle': 'Servicios',
    'services.dental.description': 'Procedimientos dentales, tratamientos de salud oral, y cuidado dental',
    'services.aesthetic.title': 'Estético',
    'services.aesthetic.subtitle': 'Servicios',
    'services.aesthetic.description': 'Procedimientos cosméticos, tratamientos de belleza, y medicina estética',

    // Common
    'common.home': 'Inicio',

    // Step Hero (ES)
    'page.index.stepHero.badge': 'Inicio rápido',
    'page.index.stepHero.title': 'Obtén tu Evaluación Digital AI en 4 Pasos',
    'page.index.stepHero.subtitle': 'De evaluación a implementación — descubre tu potencial de crecimiento.',
    'page.index.stepHero.primaryCta': 'Reservar Llamada',
    'page.index.stepHero.primaryCtaAria': 'Reservar una llamada de consulta',
    'page.index.stepHero.secondaryCta': 'Ver demo',
    'page.index.stepHero.secondaryCtaAria': 'Ir a la demo',
    'page.index.stepHero.stepsAria': 'Pasos del proceso de evaluación',
    'page.index.stepHero.steps.1.title': 'Completa el Formulario de Evaluación',
    'page.index.stepHero.steps.1.desc': 'Llena nuestro cuestionario completo sobre tu presencia digital',
    'page.index.stepHero.steps.1.cta': 'Comenzar Evaluación',
    'page.index.stepHero.steps.2.title': 'Recibe el Reporte de Evaluación AI',
    'page.index.stepHero.steps.2.desc': 'Obtén tu análisis detallado con insights accionables',
    'page.index.stepHero.steps.2.cta': 'Ver Reporte Ejemplo',
    'page.index.stepHero.steps.3.title': 'Agenda Llamada 1-1',
    'page.index.stepHero.steps.3.desc': 'Revisa cada paso con nuestro equipo experto',
    'page.index.stepHero.steps.3.cta': 'Reservar Llamada',
    'page.index.stepHero.steps.4.title': 'Lanzar y Crecer',
    'page.index.stepHero.steps.4.desc': 'Implementa tu estrategia personalizada y escala tu práctica',
    'page.index.stepHero.steps.4.cta': 'Empezar',
    
    // Audit Report translations (ES)
    'audit.report.title': 'Tu Reporte de Diagnóstico de IA',
    'audit.report.complete': 'Diagnóstico Completo',
    'audit.report.downloadPDF': 'Descargar PDF',
    'audit.report.aeoScore': 'Puntuación AEO',
    'audit.report.recommendations': 'Principales Recomendaciones',
    
    // Interactive Audit Steps translations (ES)
    'audit.steps.practiceSize.title': 'Tamaño de la Práctica',
    'audit.steps.practiceSize.description': '¿Cuántos doctores trabajan en su práctica?',
    'audit.steps.practiceSize.options.small': '1-2 Doctores',
    'audit.steps.practiceSize.options.smallDesc': 'Práctica pequeña',
    'audit.steps.practiceSize.options.medium': '3-5 Doctores',
    'audit.steps.practiceSize.options.mediumDesc': 'Práctica mediana',
    'audit.steps.practiceSize.options.large': '6+ Doctores',
    'audit.steps.practiceSize.options.largeDesc': 'Práctica grande',
    
    'audit.steps.challenges.title': 'Desafíos Actuales',
    'audit.steps.challenges.description': '¿Cuál es su mayor desafío ahora mismo?',
    'audit.steps.challenges.options.acquisition': 'Adquisición de Pacientes',
    'audit.steps.challenges.options.acquisitionDesc': 'Conseguir nuevos pacientes',
    'audit.steps.challenges.options.management': 'Gestión de Citas',
    'audit.steps.challenges.options.managementDesc': 'Programación y reservas',
    'audit.steps.challenges.options.retention': 'Retención de Pacientes',
    'audit.steps.challenges.options.retentionDesc': 'Mantener pacientes existentes',
    'audit.steps.challenges.options.roi': 'ROI de Marketing',
    'audit.steps.challenges.options.roiDesc': 'Gasto de marketing ineficaz',
    
    'audit.steps.experience.title': 'Experiencia con IA',
    'audit.steps.experience.description': '¿Qué tan familiarizado está con las herramientas de IA en salud?',
    'audit.steps.experience.options.none': 'Sin Experiencia',
    'audit.steps.experience.options.noneDesc': 'Nunca he usado herramientas de IA',
    'audit.steps.experience.options.basic': 'Conocimiento Básico',
    'audit.steps.experience.options.basicDesc': 'Algo de conocimiento sobre IA',
    'audit.steps.experience.options.experienced': 'Experimentado',
    'audit.steps.experience.options.experiencedDesc': 'Actualmente uso herramientas de IA',
    
    // Aviation page
    'pages.aviation.seo.title': 'Aviación - Academia de Pilotos RBI AI',
    'pages.aviation.seo.description': 'Plataforma de entrenamiento de aviación y educación de pilotos con IA',
    
    // AI Consultancy Page
    'aiConsultancy.page.title': 'Consultoría AI - Implementación Estratégica de IA para Salud | RBI AI',
    'aiConsultancy.page.description': 'Estrategia experta de IA y guía de implementación para prácticas médicas. Obtenga análisis integral de ROI, aseguramiento de cumplimiento y planificación paso a paso de adopción de IA.',
    'aiConsultancy.hero.badge': 'Guía Estratégica de IA',
    'aiConsultancy.hero.title': 'Transforme Su Práctica con Consultoría Experta en IA',
    'aiConsultancy.hero.subtitle': 'Planificación estratégica, hojas de ruta de implementación y optimización de ROI para una adopción exitosa de IA en su práctica médica.',
    'aiConsultancy.hero.cta': 'Agendar Consulta',
    'aiConsultancy.services.title': 'Servicios Integrales de Consultoría en IA',
    'aiConsultancy.services.subtitle': 'Guía de extremo a extremo para su viaje de transformación con IA',
    'aiConsultancy.features.strategy.title': 'Desarrollo de Estrategia de IA',
    'aiConsultancy.features.strategy.desc': 'Hojas de ruta personalizadas de IA alineadas con los objetivos de su práctica, demografía de pacientes y objetivos de crecimiento.',
    'aiConsultancy.features.assessment.title': 'Evaluación Tecnológica',
    'aiConsultancy.features.assessment.desc': 'Auditoría integral de sus sistemas actuales, identificando oportunidades de integración de IA y brechas tecnológicas.',
    'aiConsultancy.features.roi.title': 'Análisis y Pronóstico de ROI',
    'aiConsultancy.features.roi.desc': 'Modelado financiero detallado mostrando retornos esperados, ahorros de costos y crecimiento de ingresos de la implementación de IA.',
    'aiConsultancy.features.implementation.title': 'Planificación de Implementación',
    'aiConsultancy.features.implementation.desc': 'Planes de ejecución paso a paso, gestión de cronogramas y asignación de recursos para un despliegue fluido de IA.',
    'aiConsultancy.features.innovation.title': 'Asesoría en Innovación',
    'aiConsultancy.features.innovation.desc': 'Manténgase a la vanguardia con insights sobre tecnologías emergentes de IA, tendencias de la industria y ventajas competitivas.',
    'aiConsultancy.features.compliance.title': 'Aseguramiento de Cumplimiento',
    'aiConsultancy.features.compliance.desc': 'Asegure que todas las implementaciones de IA cumplan con HIPAA, GDPR y otros requisitos regulatorios desde el primer día.',
    'aiConsultancy.process.title': 'Nuestro Proceso de Consultoría',
    'aiConsultancy.process.step1.title': 'Descubrimiento y Evaluación',
    'aiConsultancy.process.step1.desc': 'Inmersión profunda en las operaciones de su práctica, desafíos y preparación para IA con entrevistas integrales a las partes interesadas.',
    'aiConsultancy.process.step2.title': 'Desarrollo de Estrategia',
    'aiConsultancy.process.step2.desc': 'Crear una hoja de ruta de IA personalizada con iniciativas priorizadas, cronogramas y métricas claras de éxito.',
    'aiConsultancy.process.step3.title': 'Soporte de Implementación',
    'aiConsultancy.process.step3.desc': 'Guíe a su equipo a través del despliegue con soporte práctico, coordinación de capacitación y gestión del cambio.',
    'aiConsultancy.process.step4.title': 'Optimización y Crecimiento',
    'aiConsultancy.process.step4.desc': 'Monitoreo continuo, optimización del rendimiento y guía estratégica a medida que maduran sus capacidades de IA.',
    'aiConsultancy.cta.title': '¿Listo para Construir Su Estrategia de IA?',
    'aiConsultancy.cta.subtitle': 'Agende una consulta para discutir las oportunidades y desafíos únicos de IA de su práctica.',
    'aiConsultancy.cta.button': 'Reservar Sesión de Estrategia',

    // Staff Training Page
    'staffTraining.page.title': 'Capacitación en IA - Programas de Adopción de IA para Salud | RBI AI',
    'staffTraining.page.description': 'Programas integrales de capacitación en IA para personal médico. Genere confianza, impulse la adopción y maximice el ROI con talleres prácticos y cursos de certificación.',
    'staffTraining.hero.badge': 'Empodere a Su Equipo',
    'staffTraining.hero.title': 'Programas de Capacitación en IA para Equipos de Salud',
    'staffTraining.hero.subtitle': 'Equipe a su personal con el conocimiento y las habilidades para aprovechar la IA de manera efectiva, aumentando las tasas de adopción y maximizando el valor de sus inversiones en IA.',
    'staffTraining.hero.cta': 'Explorar Programas de Capacitación',
    'staffTraining.programs.title': 'Programas de Capacitación Integrales',
    'staffTraining.programs.subtitle': 'Desde fundamentos de IA hasta estrategias avanzadas de implementación',
    'staffTraining.programs.fundamentals.title': 'Fundamentos de IA para Salud',
    'staffTraining.programs.fundamentals.desc': 'Introducción a conceptos de IA, casos de uso en salud y comprensión de cómo la IA puede mejorar el cuidado del paciente y las operaciones.',
    'staffTraining.programs.workflows.title': 'Flujos de Trabajo Mejorados con IA',
    'staffTraining.programs.workflows.desc': 'Aprenda a integrar herramientas de IA en operaciones diarias, optimizar el compromiso del paciente y automatizar tareas rutinarias de manera efectiva.',
    'staffTraining.programs.tools.title': 'Capacitación Práctica en Herramientas',
    'staffTraining.programs.tools.desc': 'Capacitación práctica en plataformas específicas de IA, sistemas CRM, agentes conversacionales y paneles de análisis.',
    'staffTraining.programs.advanced.title': 'Implementación Avanzada de IA',
    'staffTraining.programs.advanced.desc': 'Inmersión profunda en estrategias complejas de IA, construcción de automatización personalizada y toma de decisiones basada en datos.',
    'staffTraining.programs.certification.title': 'Programa de Certificación en IA',
    'staffTraining.programs.certification.desc': 'Obtenga credenciales reconocidas que demuestren competencia en herramientas de IA para salud y mejores prácticas.',
    'staffTraining.programs.leadership.title': 'Liderazgo y Gestión del Cambio',
    'staffTraining.programs.leadership.desc': 'Capacitación para líderes de práctica sobre impulsar la adopción de IA, gestionar el cambio y construir una cultura preparada para IA.',
    'staffTraining.benefits.title': 'Beneficios de la Capacitación',
    'staffTraining.benefits.adoption': 'Tasas de adopción de IA más rápidas en todo su equipo',
    'staffTraining.benefits.efficiency': 'Eficiencia operativa mejorada a través de la utilización adecuada de herramientas',
    'staffTraining.benefits.confidence': 'Mayor confianza del personal en el uso de tecnologías de IA',
    'staffTraining.benefits.innovation': 'Cultura de innovación y mejora continua',
    'staffTraining.benefits.retention': 'Mayor retención del personal a través del desarrollo profesional',
    'staffTraining.benefits.roi': 'ROI maximizado en inversiones de IA a través de implementación capacitada',
    'staffTraining.format.title': 'Formatos de Capacitación Flexibles',
    'staffTraining.format.onsite.title': 'Talleres Presenciales',
    'staffTraining.format.onsite.desc': 'Sesiones de capacitación interactivas y prácticas realizadas en su práctica para un aprendizaje en equipo enfocado.',
    'staffTraining.format.virtual.title': 'Sesiones Virtuales',
    'staffTraining.format.virtual.desc': 'Capacitación en vivo en línea con interacción en tiempo real, perfecta para equipos distribuidos o horarios ocupados.',
    'staffTraining.format.hybrid.title': 'Programas Híbridos',
    'staffTraining.format.hybrid.desc': 'Combinación de lanzamientos presenciales, sesiones virtuales y módulos a su propio ritmo para máxima flexibilidad.',
    'staffTraining.cta.title': 'Invierta en las Habilidades de IA de Su Equipo',
    'staffTraining.cta.subtitle': 'Contáctenos para diseñar un programa de capacitación personalizado adaptado a las necesidades y objetivos de su práctica.',
    'staffTraining.cta.button': 'Solicitar Propuesta de Capacitación',
  }
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('lang');
      if (stored === 'en' || stored === 'es') return stored;
      // Default to Spanish
      return 'es';
    }
    return 'es';
  });

  const [investorName, setInvestorNameState] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('investorName') || '';
    }
    return '';
  });

  const setInvestorName = (name: string) => {
    setInvestorNameState(name);
    if (typeof window !== 'undefined') {
      localStorage.setItem('investorName', name);
    }
  };

  useEffect(() => {
    try {
      localStorage.setItem('lang', language);
    } catch {}
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', language);
    }
  }, [language]);

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations['en']] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, investorName, setInvestorName, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};